import { Account } from '@/domain/usecases'
import { i18nTranslationHelper } from '@/i18n'
import { AccountStore } from '@/infra/tracking/account-store'
import {
  makeRemoteAccountProfile,
  makeRemoteUserProfileAssessment
} from '@/main/factories/usecases'
import { redirectToLogin } from '@/data/services/login/login-service-utils'
import { getOrCreateSessionId, getSessionId } from '@/data/services/login/session-id-store'
import {
  FeatureFlagClient,
  FeatureFlagName,
  getFlagValue,
  useFeatureFlagSync
} from '@/infra/feature-flag'
import { enrichAccountProfileContext } from '@/presentation/helpers/account-profile-helper'
import errorMonitor from '@/infra/tracking/error-monitor'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AnalyticsTracker } from '@/infra/tracking'
// import { loginService } from '@/data/services/login/login-service'
import { PathConstants } from '@/main/routes/paths'

type AccountProviderProps = {
  children: ReactNode
  initialState?: AccountContextValueType
  onCompanyLoaded?: (companyUniqueId: string) => void
  isNested?: boolean
}

export type NoPlanConfigType = {
  message?: string
  ctaLink?: string
  ctaLabel?: string
}

export type HelpCenterConfigType = {
  companyId?: string
  redirectTo: string
  hasChatBot: boolean
  hasLink: boolean
  hasWhatsApp: boolean
  whatsappPhone?: string
  companyName?: string
  noPlanConfig?: NoPlanConfigType
}

export type AccountWithCustomProperties = Account.Model & {
  displayName: string
  helpCenterConfig: HelpCenterConfigType
}
type AccountContextValueType = AccountWithCustomProperties | null

export type AccountContextType =
  | null
  | [AccountContextValueType, Dispatch<SetStateAction<AccountContextValueType>>]

export type ValidAccountState = [
  AccountWithCustomProperties,
  Dispatch<SetStateAction<AccountWithCustomProperties>>
]

export const AUTH_REDIRECT_URI = 'auth_redirect_uri'

const AccountContext = createContext({} as AccountContextType)

const changeAppLanguageByUserPreference = (language: string) => {
  const currentLanguage = i18nTranslationHelper.getCurrentLanguage()
  const supportedLanguages = i18nTranslationHelper.getAllLanguages()

  if (!supportedLanguages.includes(language)) {
    return
  }

  if (currentLanguage === language) {
    return
  }

  i18nTranslationHelper.changeLanguage(language).then(() => {
    window.location.reload()
  })
}

const AccountProvider = ({
  initialState = null,
  children,
  onCompanyLoaded,
  isNested = false
}: AccountProviderProps) => {
  const [account, setAccount] = useState<AccountContextValueType>(initialState)
  const isAnonymous = PathConstants.isInStaticRoute() && !getSessionId()
  const queryClient = useQueryClient()
  const remoteAccountProfile = makeRemoteAccountProfile(queryClient)
  const remoteUserProfileAssessment = makeRemoteUserProfileAssessment(queryClient)
  // Sincroniza o cliente LD com FeatureFlagClient. Deve ser o primeiro hook declarado
  // para garantir que state.client esteja pronto antes de identifyUser() ser chamado.
  useFeatureFlagSync()
  const isInitialized = useRef(false)
  const isRedirecting = useRef(false)

  /**
   * Initializes all integrations (LaunchDarkly, Sentry, Analytics)
   * This runs regardless of whether data came from API or loader
   */
  const initializeIntegrations = async (accountData: Account.Model) => {
    // Identifica o usuário no LaunchDarkly — client já disponível via useFeatureFlagSync
    await FeatureFlagClient.identifyUser(accountData)

    // Identify user in Sentry for error tracking
    errorMonitor.identifyUser(accountData)

    // Identify user in Analytics (GA, GTM, etc.)
    AnalyticsTracker.identifyUser(accountData)
  }

  /**
   * Completes account setup after data is available
   * Enriches account, updates store, changes language, fetches assessment
   */
  const completeAccountSetup = async (accountData: Account.Model) => {
    getOrCreateSessionId()
    const responseProfile = await remoteUserProfileAssessment.getLatest().catch(() => {
      // If assessment fetch fails, we still want to proceed with login
      console.warn('[AccountProvider] Failed to fetch latest profile assessment')
      return null
    })
    const companies = getFlagValue(
      FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER
    ) as readonly HelpCenterConfigType[]
    const enrichedAccount = enrichAccountProfileContext(accountData, companies)

    const updatedAccount = {
      ...enrichedAccount,
      latestProfileAssessment: responseProfile || undefined
    }

    setAccount(updatedAccount)
    AccountStore.setCurrentAccount(updatedAccount)
    onCompanyLoaded?.(updatedAccount.company.uniqueId)

    // AccountProvider existe em dois contextos:
    // - Root (initialState = null): representa o usuário logado, carregado via API.
    //   É o único com acesso ao preferenceLanguage real — portanto o único autorizado
    //   a alterar o idioma da aplicação.
    // - Nested (initialState != null): criado por AccountPreRender para prover contexto
    //   de conta em páginas que operam sobre perfis sintéticos (ex: formulário de criação
    //   de colaborador). O accountProfile nesses casos tem preferenceLanguage vazio,
    //   o que causaria conflito com o idioma já definido pelo root e dispararia
    //   reloads em loop ('' → 'pt' → colide com 'es' do root → reload infinito).
    if (!isNested) {
      handleRedirectUri(updatedAccount.preferenceLanguage || 'pt')
    }
  }

  /**
   * Checks for any pending redirect URI (set before login) and redirects the user
   * This ensures that after login, users are taken to their intended destination instead of the homepage
   */
  const handleRedirectUri = (preferenceLanguage: string) => {
    const pendingRedirect = sessionStorage.getItem(AUTH_REDIRECT_URI)
    if (pendingRedirect) {
      sessionStorage.removeItem(AUTH_REDIRECT_URI)
      window.location.replace(pendingRedirect)
    } else {
      changeAppLanguageByUserPreference(preferenceLanguage)
    }
  }

  useEffect(() => {
    // Prevent double initialization
    if (isInitialized.current) {
      return
    }

    const initializeAccount = async () => {
      // const isLogged = await loginService.isLoggedUser()

      if (isAnonymous) {
        // LaunchDarkly is already initialized with anonymous context in main.tsx
        console.log('[AccountProvider] Setting anonymous account')
        const anonAccount = Account.getAnonymous()
        setAccount(anonAccount)
        AccountStore.setCurrentAccount(anonAccount)
        isInitialized.current = true
        return
      }

      // Mark as initialized immediately to prevent double-execution
      // caused by React re-renders during async awaits below
      isInitialized.current = true

      try {
        // Path A: Initial state provided from loader (AccountPreRender)
        if (initialState && !Account.isAnonymous(initialState)) {
          // Initialize integrations with the pre-loaded data
          await initializeIntegrations(initialState)

          // Complete setup (enrichment already done in AccountPreRender, but ensures consistency)
          await completeAccountSetup(initialState)

          console.log('[AccountProvider] ✅ All services initialized successfully (from loader)')
          return
        }

        // Path B: No initial state, fetch from API
        if (!account) {
          const data = await remoteAccountProfile.loaderGet()

          // Initialize integrations
          await initializeIntegrations(data)

          // Complete setup
          await completeAccountSetup(data)

          console.log('[AccountProvider] ✅ All services initialized successfully (from API)')
        }
      } catch (error) {
        // Prevent multiple redirects
        if (isRedirecting.current) {
          return
        }

        console.warn('[AccountProvider] Error initializing account, redirecting to login')
        isRedirecting.current = true

        setTimeout(() => {
          redirectToLogin(window.location.href)
        }, 100)
      }
    }

    initializeAccount()
  }, [account, initialState])

  return <AccountContext.Provider value={[account, setAccount]}>{children}</AccountContext.Provider>
}

export { AccountContext, AccountProvider }
